<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { $bus } from '@/common/events'
import { $router } from '@/router'
import { env } from '@/secrets/env'
import { useTranslate } from '@/composables/language'
import { useInterrupt } from '@/composables/interrupt'
import { useSessionTimer } from '@/composables/session'
import { useSystemStatus } from '@/composables/system'

const { culture } = useTranslate();

const props = defineProps({
  showIp: {
    type: Boolean,
    default: true
  },
  showVersion: {
    type: Boolean,
    default: env.VITE_USE_CASE == "gloves" ? false : true
  },
  showTime: {
    type: Boolean,
    default: true
  },
  extended: {
    type: Boolean,
    default: false
  }
});

let fastTimer = null;

const time = ref("");
const status = ref({ });
const version = ref(env.VITE_SEMVER);

let activeIndex = -1;
const iface = ref();
const network = ref([ ]);

const ifaceIcon = computed(() => {
  if (iface.value && iface.value.type) {
    switch (iface.value.type) {
      case "ethernet":
        return "fa-network-wired";

      case "wifi":
        return "fa-wifi";

      case "mobile":
        return "fa-signal";

      case "vpn":
        return "fa-shield-halved";
    }
  }

  //Default
  return "fa-network-wired";
})

const hasSessionExpired = ref(false);
const willExpireSoonDuration = ref(null);
const sessionExpiredDuration = ref(null);

const keyValuePairs = ref(null);

const { enabled, context } = useSessionTimer();
const { allowInterruption, canPostponeAction } = useInterrupt();
const { latestStatus } = useSystemStatus();

/*
const debugVariables = computed(() => {
  let result = {
    eth0: status.value?.ethernet?.ipv4,
    wlan0: status.value?.wifi?.ipv4,
    usb0: status.value?.mobile?.ipv4,
    ver: version.value,
    now: time.value,
    ses: sessionExpiredDuration.value ? parseFloat(sessionExpiredDuration.value / 1000).toFixed(3) + "s" : "",
    int: allowInterruption.value,
    kvp: keyValuePairs.value
  };
  return result;
});
*/

onMounted(() => {
  updateStatus(latestStatus.value);

  fastTimer = setInterval(() => updateTime(), env.DEV ? 200 : 1000);
  updateTime();

  $bus.on("debug footer", (data) => {
    keyValuePairs.value = data ? Object.keys(data).map(x => {
      return {
        name: x,
        value: data[x]
      };
    }) : null;
  })
});

onUnmounted(() => {
  clearInterval(fastTimer);
});

watch(latestStatus, (value) => {
  updateStatus(value);
});

async function updateStatus(data) {
  try {
    //Get status
    status.value = data;
    network.value = data.network;

    let activeConnections = data.network.filter(x => x.ipv4);
    if (activeConnections.length > 0) {
      activeIndex++;
      if (activeIndex >= activeConnections.length) {
        activeIndex = 0;
      }
      iface.value = activeConnections[activeIndex];
    }
    else {
      activeIndex = -1;
      iface.value = null;
    }
  }
  catch (err) {
    //console.error(err);
  }
}

function updateTime() {
  let date = new Date();
  //let parts = date.toISOString().replace(/\.\d{3}Z$/, "").split("T");
  //time.value = parts.join(" "); //UTC time
  //time.value = date.toLocaleString("sl-SI", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  time.value = date.toLocaleString(culture.value).replace(/\.\s+/g, ".").replace(",", " "); //Local time

  //Session timer
  if (enabled.value) {
    //Only if warning message is enabled
    if (context?.value?.sessionWarningAt) {
      willExpireSoonDuration.value = -1 * Math.floor(date.subtract(context.value.sessionWarningAt)); //TODO: formatDuration
    }

    //Session expiry countdown
    if (context?.value?.sessionExpiresAt) {
      sessionExpiredDuration.value = -1 * Math.floor(date.subtract(context.value.sessionExpiresAt)); //TODO: formatDuration
    }
  }
}
</script>

<template>
  <div class="fixed bottom-0 w-full grid grid-cols-3 font-mono px-8 py-4 text-slate-500 whitespace-nowrap" style="font-size: 0.9rem;" v-if="!props.extended">
    <div class="text-left">
      <span v-if="props.showIp && iface && iface.ipv4" class="mr-10">{{ iface.ipv4 }}<i class="fa text-slate-300 ml-2" :class="ifaceIcon"></i></span>
      <!-- <span v-if="props.showIp && status?.ethernet?.ipv4" class="mr-10">{{ status?.ethernet?.ipv4 }} <i class="fa fa-network-wired text-slate-300"></i></span> -->
      <!-- <span v-if="props.showIp && status?.wifi?.ipv4">{{ status?.wifi?.ipv4 }} <i class="fa fa-wifi text-slate-300"></i></span> -->
    </div>
    <div class="text-center" v-if="props.showVersion">
      <span class="text-slate-400">{{ version }}</span>
    </div>
    <div class="text-right" v-if="props.showTime">
      <span>{{ time }}</span>
    </div>
  </div>
  <div class="debug-panel" v-else>
    <div v-if="status?.ethernet?.ipv4">eth0: {{ status?.ethernet?.ipv4 }}</div>
    <div v-if="status?.wifi?.ipv4">wlan0: {{ status?.wifi?.ipv4 }} </div>
    <div v-if="status?.mobile?.ipv4">usb0: {{ status?.mobile?.ipv4 }} </div>
    <div v-if="version">ver: {{ version }}</div>
    <div v-if="time">now: {{ time }}</div>
    <div v-if="enabled">ses: {{ parseFloat(sessionExpiredDuration / 1000).toFixed(3) }}s</div>
    <div v-if="!enabled">ses: --</div>
    <div v-if="allowInterruption">int: true</div>
    <div v-if="!allowInterruption">int: false</div>
    <div v-if="keyValuePairs" v-for="item in keyValuePairs">{{ item.name }}: {{ item.value }}</div>
  </div>
</template>

<style scoped>
.debug-panel {
  @apply fixed bottom-0 right-0 max-w-full grid font-mono px-2 py-1 z-30 opacity-90 text-amber-700 whitespace-nowrap mb-2 ml-20 mr-2 border border-amber-500 shadow-md shadow-amber-800 bg-amber-100;
  font-size: 0.75rem;
}
</style>
